import { useCallback, useEffect, useRef } from 'react';
import { driver, type Driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import {
  buildClientWorkspaceTourSteps,
  hasSeenClientWorkspaceTour,
  markClientWorkspaceTourSeen,
} from '../../utils/clientWorkspaceTourSteps';
import './LeadsHubTour.css';

export function useClientWorkspaceTour(enabled: boolean) {
  const driverRef = useRef<Driver | null>(null);
  const autoStartedRef = useRef(false);

  const destroyTour = useCallback(() => {
    driverRef.current?.destroy();
    driverRef.current = null;
  }, []);

  const startTour = useCallback(() => {
    destroyTour();
    const steps = buildClientWorkspaceTourSteps();
    if (steps.length === 0) return false;

    const driverObj = driver({
      steps,
      showProgress: steps.length > 2,
      progressText: '{{current}} of {{total}}',
      nextBtnText: 'Next',
      prevBtnText: 'Back',
      doneBtnText: 'Got it',
      popoverClass: 'lh-tour-popover',
      smoothScroll: true,
      stagePadding: 8,
      stageRadius: 6,
      allowClose: true,
      overlayOpacity: 0.45,
      onNextClick: (_el, _step, { driver: d }) => {
        if (d.isLastStep()) {
          d.destroy();
          return;
        }
        d.moveNext();
      },
      onCloseClick: (_el, _step, { driver: d }) => d.destroy(),
      onDestroyed: () => {
        driverRef.current = null;
        markClientWorkspaceTourSeen();
      },
    });

    driverRef.current = driverObj;
    driverObj.drive();
    return true;
  }, [destroyTour]);

  useEffect(() => () => destroyTour(), [destroyTour]);

  useEffect(() => {
    if (!enabled || autoStartedRef.current || hasSeenClientWorkspaceTour()) return;
    autoStartedRef.current = true;
    const timer = window.setTimeout(() => startTour(), 900);
    return () => window.clearTimeout(timer);
  }, [enabled, startTour]);

  return { startTour };
}
